import { ReactNode, useMemo } from 'react';
import styled from '@emotion/styled';
import { lighten } from '@mui/material/styles';
import { Card, CardContent, CardHeader, Fade, Grid } from '@mui/material';

import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { TitleEntity } from '@/redux/slices/slice_types';
import { TitleEntityV2 } from '@/redux/slices/titleV2Slice.types';
import { setVersionSelections } from '@/redux/slices/orderPlacementSlice';
import { selectFocusNewScheduleView } from '@/redux/selectors/focusNewScheduleSelectors';
import { TabBadge } from '@/components/_common';
import SectionHeading from '@/components/_common/SectionHeading';
import OPSelectionsEmpty from '@/components/OrderPlacement/OPSelectionsEmpty';
import ScheduleSelectedVersions from '@/components/Focus/AvailsSchedule/NewSchedule/TitleSearch/ScheduleSelectedVersions/ScheduleSelectedVersions';
import { IncrementalLaunchSelectedVersions } from '@/components/Focus/AvailsSchedule/NewSchedule/ReviewSchedule/AddIncrementalLaunch/IncrementalLaunchWorkspace';
import { selectVersions } from '@/redux/selectors/orderPlacementSelectors';

type ScheduleSelectionsProps = {
  children?: ReactNode;
  incrementalLaunchVersions?: IncrementalLaunchSelectedVersions;
  onSelectedVersionsChange?: (newIncrementalLaunchSelectedVersions: IncrementalLaunchSelectedVersions) => void;
  previousSelectedVersions?: (TitleEntity | TitleEntityV2)['id'][];
  readOnly?: boolean;
};

const ScheduleSelections = ({
  children,
  incrementalLaunchVersions,
  onSelectedVersionsChange,
  previousSelectedVersions,
  readOnly = false
}: ScheduleSelectionsProps) => {
  const dispatch = useAppDispatch();
  const view = useAppSelector(selectFocusNewScheduleView);
  const selectedVersions = useAppSelector(selectVersions);
  const isSearchView = view === 'search';
  const isDetailsView = view === 'details';
  const isReviewView = view === 'review';

  const updateSelectedVersions = (version: TitleEntity | TitleEntityV2) => {
    if (readOnly) return;

    if (onSelectedVersionsChange && incrementalLaunchVersions) {
      if (incrementalLaunchVersions.add.includes(version.id)) {
        onSelectedVersionsChange({
          ...incrementalLaunchVersions,
          add: incrementalLaunchVersions.add.filter((v) => v !== version.id)
        });
        return;
      }

      if (incrementalLaunchVersions.remove.includes(version.id)) {
        onSelectedVersionsChange({
          ...incrementalLaunchVersions,
          remove: incrementalLaunchVersions.remove.filter((v) => v !== version.id)
        });
        return;
      }

      if (previousSelectedVersions?.includes(version.id)) {
        onSelectedVersionsChange({
          ...incrementalLaunchVersions,
          remove: [...incrementalLaunchVersions.remove, version.id]
        });
        return;
      }

      onSelectedVersionsChange({
        ...incrementalLaunchVersions,
        add: [...incrementalLaunchVersions.add, version.id]
      });
      return;
    }

    dispatch(setVersionSelections(version));
  };

  const selectedVersionsMemo = useMemo((): (TitleEntity | TitleEntityV2)['id'][] => {
    if (!previousSelectedVersions || !incrementalLaunchVersions) {
      return selectedVersions.map((sv) => sv.id);
    }

    const originalVersions = previousSelectedVersions.filter((pv) => !incrementalLaunchVersions.remove.includes(pv));
    const addedSelectedVersions = incrementalLaunchVersions.add.filter((av) => !originalVersions.includes(av));

    return [...originalVersions, ...addedSelectedVersions];
  }, [previousSelectedVersions, incrementalLaunchVersions, selectedVersions]);

  return (
    <StyledGrid xs={12} md={isSearchView ? 5 : 4} item>
      <Fade in timeout={900}>
        <SelectedVersionsCard elevation={0}>
          <SelectedVersionsHeader
            title={
              <StyledSectionHeading color="secondary">
                {isSearchView ? 'Selected Versions' : 'Selections'}
                {isSearchView && <TabBadge color="secondary" count={selectedVersions.length} />}
              </StyledSectionHeading>
            }
          />
          <CardContent sx={{ pt: selectedVersions.length ? 1 : 0 }}>
            {!selectedVersions.length ? (
              <OPSelectionsEmpty text="Search for a title to begin adding versions" />
            ) : (
              <>
                <ScheduleSelectedVersions selectedVersions={selectedVersionsMemo} onVersionClick={updateSelectedVersions} />
                {(isDetailsView || isReviewView) && children}
              </>
            )}
          </CardContent>
        </SelectedVersionsCard>
      </Fade>
    </StyledGrid>
  );
};

export default ScheduleSelections;

const StyledGrid = styled(Grid)(({ theme }) => ({
  position: 'sticky',
  alignSelf: 'start',
  top: theme.spacing(-5)
}));

const SelectedVersionsCard = styled(Card)(({ theme }) => ({
  backgroundColor: theme.palette.mode === 'dark' ? '#1A1F2A' : lighten('#1A1F2A', 0.99)
}));

const SelectedVersionsHeader = styled(CardHeader)(({ theme }) => ({
  color: theme.palette.background.paper
}));

const StyledSectionHeading = styled(SectionHeading)(({ theme }) => ({
  marginBottom: theme.spacing(1)
}));
